import React from "react";
import {
  useTable,
  useFilters,
  useGlobalFilter,
  useSortBy,
  usePagination,
  TableOptions,
  UseSortByState,
} from "react-table";
import { useLocalStorage } from "../../hooks";
import { Card, CardBody, CardHeader } from "../Cards";
import { ColumnFilterList } from "./Filters/ColumnFilterList";
import { DefaultColumnFilter } from "./Filters/DefaultColumnFilter";
import GlobalFilter from "./Filters/GlobalFilter";
import { DataTablePagination } from "./DataTablePagination";
import { Table, TableProps } from "./Table";
import { DataTableHeader } from "./DataTableHeader";
import { DataTableBody } from "./DataTableBody";
import "../../../scss/_table.css";

export type DataTableProps<T extends object = {}> = TableOptions<T> &
  Partial<UseSortByState<T>> &
  Omit<TableProps, "card"> & {
    actions?: React.ReactNode;
    columnFilter?: boolean;
    globalFilter?: boolean;
    name: string;
    rowStyle?: (row: T) => string | undefined;
    title?: string;
  };

export const DataTable = <T extends object>(props: DataTableProps<T>) => {
  const {
    actions,
    columnFilter,
    globalFilter,
    name,
    rowStyle,
    sortBy,
    title,
    ...rest
  } = props;
  const [pageSize, setPageSize] = useLocalStorage(`${name}_pageSize`, 10);
  const data = React.useMemo(() => props.data ?? [], [props.data]);
  const columns = React.useMemo(() => props.columns, [props.columns]);

  const instance = useTable<T>(
    {
      columns: columns,
      data,
      isMultiSortEvent: (e) => e.ctrlKey,
      initialState: {
        pageSize: pageSize,
        sortBy: sortBy ?? [],
      },
      defaultColumn: { Filter: DefaultColumnFilter },
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    state: { filters },
  } = instance;

  return (
    <Card>
      {(title || globalFilter) && (
        <CardHeader>
          <h3 className="card-title">{title}</h3>
          <div className="card-actions">
            {actions}
            {globalFilter && <GlobalFilter {...instance} />}
          </div>
        </CardHeader>
      )}
      {columnFilter && filters.length > 0 && (
        <CardBody>
          <ColumnFilterList {...instance} />
        </CardBody>
      )}
      <div className="table-responsive">
        <Table {...rest} {...getTableProps()} className="data-table" card>
          <DataTableHeader {...instance} columnFilter={columnFilter} />
          <DataTableBody {...instance} rowStyle={rowStyle} />
        </Table>
      </div>
      {data.length > 0 && (
        <DataTablePagination {...instance} setPageSizeFn={setPageSize} />
      )}
    </Card>
  );
};

export default DataTable;
